import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, Loader2, RefreshCw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { fetchArtifactSvg } from '@/artifactEditor/services/artifactApi';

const THEMES = ['educational', 'light', 'dark', 'corporate'];

function SvgPreviewFrame({ svg, loading, error, onRetry }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border border-sky-500/20 bg-slate-900/50 py-16 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
        Rendering SVG preview…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-amber-500/25 bg-amber-950/15 px-4 py-6 space-y-3">
        <p className="text-sm text-amber-200/90">{error}</p>
        <p className="text-[11px] text-slate-500">
          Artifacts must be published in Step 5C.2 before SVG preview is available.
        </p>
        {onRetry ? (
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={onRetry}>
            <RefreshCw className="w-3 h-3 mr-1.5" />
            Retry
          </Button>
        ) : null}
      </div>
    );
  }

  if (!svg) {
    return (
      <p className="text-sm text-slate-500 py-8 text-center rounded-lg border border-slate-700/50 bg-slate-900/30">
        Select an artifact to preview rendered SVG.
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-sky-500/25 bg-slate-950/40 overflow-hidden">
      <div className="flex items-center justify-center min-h-[280px] max-h-[55vh] p-4 bg-gradient-to-b from-slate-100 to-slate-200/90">
        <div
          className="w-full h-full flex items-center justify-center [&_svg]:block [&_svg]:max-w-full [&_svg]:max-h-[min(48vh,520px)] [&_svg]:w-auto [&_svg]:h-auto [&_svg]:object-contain"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </div>
  );
}

export function ArtifactSvgRendererV3Panel({ data, onArtifactSelect }) {
  const entries = data?.render_entries || [];
  const [selectedId, setSelectedId] = useState('');
  const [theme, setTheme] = useState('light');
  const [svg, setSvg] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedEntry = useMemo(
    () => entries.find((e) => e.artifact_id === selectedId) || entries[0] || null,
    [entries, selectedId],
  );

  useEffect(() => {
    if (!entries.length) {
      setSelectedId('');
      return;
    }
    if (!selectedId || !entries.some((e) => e.artifact_id === selectedId)) {
      setSelectedId(entries[0].artifact_id);
    }
  }, [entries, selectedId]);

  const loadPreview = useCallback(async () => {
    const aid = selectedEntry?.artifact_id;
    if (!aid) {
      setSvg('');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const rendered = await fetchArtifactSvg(aid, { theme });
      if (!rendered?.includes('<svg')) {
        throw new Error('Render API returned an empty or invalid SVG response.');
      }
      setSvg(rendered);
    } catch (exc) {
      setSvg('');
      const msg = exc?.response?.data?.error?.message
        || exc?.response?.data?.detail
        || exc?.message
        || 'Failed to load SVG preview';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedEntry?.artifact_id, theme]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  useEffect(() => {
    onArtifactSelect?.(selectedEntry?.artifact_id || null);
  }, [onArtifactSelect, selectedEntry?.artifact_id]);

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-sky-500/25 bg-sky-950/15 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-sky-200">
          <Image className="w-4 h-4" />
          {data.total_render_entries} SVG render entries
          {data.step === '5C.5' ? (
            <span className="text-[10px] text-sky-400/70">(Step 5C.5)</span>
          ) : null}
        </div>
        <div className="text-sm text-slate-400">
          Positioned EDM → SVG (zoom, pan, animation-ready)
        </div>
      </div>

      <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3 text-[11px] text-slate-400 font-mono space-y-1">
        <p>5C.4 Layout → <span className="text-sky-300">5C.5 SVG Renderer</span> → Tutorial Player</p>
        <p>Themes: {(data.themes || []).join(' · ')}</p>
        <p>Export: {(data.export_modes || []).join(' · ')}</p>
      </div>

      {entries.length ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[10px] uppercase tracking-wide text-sky-400/80 w-full sm:w-auto">
              SVG preview
            </p>
            {entries.length > 1 ? (
              <Select value={selectedEntry?.artifact_id || ''} onValueChange={setSelectedId}>
                <SelectTrigger className="h-8 min-w-[200px] text-xs border-slate-700 bg-slate-900 text-slate-300">
                  <SelectValue placeholder="Select artifact" />
                </SelectTrigger>
                <SelectContent>
                  {entries.map((e) => (
                    <SelectItem key={e.artifact_id} value={e.artifact_id}>
                      {e.artifact_id}
                      {' · '}
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-xs text-slate-400 font-mono">{selectedEntry?.artifact_id}</span>
            )}
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger className="h-8 w-[140px] text-xs border-slate-700 bg-slate-900 text-slate-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {THEMES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-slate-400"
              disabled={loading || !selectedEntry}
              onClick={loadPreview}
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {selectedEntry ? (
            <p className="text-[10px] text-slate-500">
              {selectedEntry.name}
              <span className="mx-1">·</span>
              {selectedEntry.artifact_type}
              <span className="mx-1">·</span>
              v{selectedEntry.current_version}
            </p>
          ) : null}

          <SvgPreviewFrame svg={svg} loading={loading} error={error} onRetry={loadPreview} />
        </div>
      ) : (
        <p className="text-sm text-slate-500">No render entries yet.</p>
      )}

      {entries.length > 1 ? (
        <ul className="space-y-1 pt-1 border-t border-slate-800/80">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">All entries</p>
          {entries.map((e) => (
            <li key={e.artifact_id}>
              <button
                type="button"
                onClick={() => setSelectedId(e.artifact_id)}
                className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${
                  e.artifact_id === selectedEntry?.artifact_id
                    ? 'border-sky-500/40 bg-sky-950/25 text-sky-100'
                    : 'border-sky-500/15 bg-sky-950/10 text-sky-100/80 hover:bg-sky-950/20'
                }`}
              >
                <span className="font-mono text-[11px] text-sky-400/90">{e.artifact_id}</span>
                <span className="mx-2 text-slate-500">·</span>
                <span className="truncate">{e.name}</span>
                <span className="text-[10px] text-slate-500 ml-2">{e.artifact_type}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
